import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import '../assets/AllTabsCommon.css';
import '../assets/StartRemoteDebug.css';

const RULE_TYPE_OPTIONS = [
    { value: 'RULE_TYPE_TRIGGER_BASED', label: 'RULE_TYPE_TRIGGER_BASED' },
    { value: 'RULE_TYPE_CRON_BASED', label: 'RULE_TYPE_CRON_BASED' },
];

const QUOTA_TYPE_MAX_SIZE = 'maxSize';
const QUOTA_TYPE_MAX_OCCURRENCE = 'maxOccurrence';

const selectStyles = {
    control: (base) => ({
        ...base,
        minHeight: '38px',
        height: '38px',
        borderColor: 'rgb(103, 114, 132)',
    }),
    valueContainer: (base) => ({
        ...base,
        height: '38px',
        padding: '0 8px',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: '38px',
    }),
    placeholder: (base) => ({
        ...base,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        margin: 0,
    }),
    singleValue: (base) => ({
        ...base,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        margin: 0,
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '14px',
        color: state.isSelected ? 'white' : 'rgba(0, 0, 0, 0.85)',
        padding: '8px 12px',
        backgroundColor: state.isSelected
            ? 'rgb(50, 83, 220)'
            : state.isFocused
            ? '#f0f4ff'
            : 'white',
    }),
};

const emptyForm = {
    ruleType: null,
    label: '',
    offHysterisisMs: '',
    onHysterisisMs: '',
    startTriggerRuleId: null,
    priority: 1,
    stopTriggerRuleId: null,
    stopTriggerDurationMs: '',
    quotaType: QUOTA_TYPE_MAX_SIZE,
    quotaMaxSize: '',
    quotaMaxOccuranceCount: '',
    quotaTimeWindowMs: '',
    quotaMaxTimeWindowMs: '',
};

function ContextCollectionRules({ userInfo, mode = 'both' }) {
    const [form, setForm] = useState(emptyForm);
    const [contextRuleIdOptions, setContextRuleIdOptions] = useState([]);
    const [rules, setRules] = useState([]);
    const [loadingRules, setLoadingRules] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [validationError, setValidationError] = useState('');
    const [expandedRuleId, setExpandedRuleId] = useState(null);

    const BASE_URL = process.env.REACT_APP_WBC_API_BASE_URL;

    useEffect(() => {
        fetchContextRuleIds();
        fetchRules();
    }, []);

    const fetchContextRuleIds = async () => {
        try {
            const res = await fetch(`${BASE_URL}/api/v1/onboarding/context-rule-ids`);
            if (!res.ok) return;
            const data = await res.json();
            const options = (data.contextRuleIds || []).map((r) => ({
                value: r.contextRuleId,
                label: `${r.contextRuleId} — ${r.description || 'Context Rule ' + r.contextRuleId}`,
            }));
            setContextRuleIdOptions(options);
        } catch (err) {
            // silently fail — dropdowns remain empty
        }
    };

    const fetchRules = async () => {
        setLoadingRules(true);
        try {
            const res = await fetch(`${BASE_URL}/api/v1/onboarding/context-collection-rules`);
            if (!res.ok) return;
            const data = await res.json();
            setRules(data.rules || []);
        } catch (err) {
            // silently fail
        } finally {
            setLoadingRules(false);
        }
    };

    const handleFieldChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const validate = () => {
        if (!form.ruleType) return 'Rule Type is required.';
        if (!form.label.trim()) return 'Label is required.';
        if (!form.startTriggerRuleId) return 'Start Trigger Rule ID is required.';
        if (!form.stopTriggerRuleId && form.stopTriggerDurationMs === '') return 'Either Stop Trigger Rule ID or Stop Trigger Duration is required.';
        return '';
    };

    const buildPayload = () => {
        const payload = {
            ruleType: form.ruleType.value,
            label: form.label.trim(),
        };
        if (form.offHysterisisMs !== '') payload.offHysterisisMs = parseInt(form.offHysterisisMs, 10);
        if (form.onHysterisisMs !== '') payload.onHysterisisMs = parseInt(form.onHysterisisMs, 10);
        if (form.priority !== '') payload.priority = parseInt(form.priority, 10);
        if (form.startTriggerRuleId) payload.startTriggerRuleId = form.startTriggerRuleId.value;
        if (form.stopTriggerRuleId) payload.stopTriggerRuleId = form.stopTriggerRuleId.value;
        if (form.stopTriggerDurationMs !== '') payload.stopTriggerDurationMs = parseInt(form.stopTriggerDurationMs, 10);

        if (form.quotaType === QUOTA_TYPE_MAX_SIZE) {
            if (form.quotaMaxSize !== '') payload.quotaMaxSize = parseInt(form.quotaMaxSize, 10);
        } else {
            if (form.quotaMaxOccuranceCount !== '') payload.quotaMaxOccuranceCount = parseInt(form.quotaMaxOccuranceCount, 10);
        }
        if (form.quotaTimeWindowMs !== '') payload.quotaTimeWindowSec = parseInt(form.quotaTimeWindowMs, 10);
        if (form.quotaMaxTimeWindowMs !== '') payload.quotaMaxTimeWindowMs = parseInt(form.quotaMaxTimeWindowMs, 10);

        return payload;
    };

    const handleSubmit = async () => {
        setSuccessMessage('');
        setErrorMessage('');
        const err = validate();
        if (err) {
            setValidationError(err);
            return;
        }
        setValidationError('');
        setSubmitting(true);
        try {
            const res = await fetch(`${BASE_URL}/api/v1/onboarding/context-collection-rule`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(buildPayload()),
            });
            if (res.ok) {
                setSuccessMessage('Context Collection Rule added successfully.');
                setForm(emptyForm);
                fetchRules();
            } else {
                let msg = 'Failed to add rule.';
                try {
                    const body = await res.json();
                    msg = body.errorMessage || msg;
                } catch (_) {}
                setErrorMessage(msg);
            }
        } catch (err) {
            setErrorMessage('Error submitting rule.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReset = () => {
        setForm(emptyForm);
        setValidationError('');
        setSuccessMessage('');
        setErrorMessage('');
    };

    const handleToggleExpand = (ruleId) => {
        setExpandedRuleId((prev) => (prev === ruleId ? null : ruleId));
    };

    const numberInput = (field, placeholder) => (
        <input
            className="file-input"
            type="number"
            min="0"
            placeholder={placeholder}
            value={form[field]}
            onChange={(e) => handleFieldChange(field, e.target.value)}
        />
    );

    const isCronBased = form.ruleType?.value === 'RULE_TYPE_CRON_BASED';

    return (
        <div style={{ padding: '0 20px' }}>
            {/* ── Add New Rule Form ── */}
            {mode !== 'view' && <div className="tab-heading" style={{ paddingBottom: '8px' }}>
                <h1>Onboard Context Collection Rule</h1>
            </div>}

            {mode !== 'view' && validationError && (
                <div className="error-banner" style={{ marginBottom: '12px' }}>
                    <div className="error-message">{validationError}</div>
                </div>
            )}
            {mode !== 'view' && successMessage && (
                <div className="success-banner" style={{ marginBottom: '12px' }}>
                    <div className="success-message">{successMessage}</div>
                </div>
            )}
            {mode !== 'view' && errorMessage && (
                <div className="error-banner" style={{ marginBottom: '12px' }}>
                    <div className="error-message">{errorMessage}</div>
                </div>
            )}

            {mode !== 'view' && <div className="row">
                <div className="dropdown-section" style={{ flex: 1 }}>
                    <div className="dropdown-heading">
                        Rule Type <span className="required-asterisk">*</span>
                    </div>
                    <Select
                        className="multi-select"
                        classNamePrefix="select"
                        styles={selectStyles}
                        options={RULE_TYPE_OPTIONS}
                        value={form.ruleType}
                        onChange={(val) => handleFieldChange('ruleType', val)}
                        placeholder="Select Rule Type"
                        isClearable
                    />
                </div>
                <div className="dropdown-section" style={{ flex: 1 }}>
                    <div className="dropdown-heading">
                        Label <span className="required-asterisk">*</span>
                    </div>
                    <input
                        className="file-input"
                        type="text"
                        placeholder="eg. High CPU Trigger"
                        value={form.label}
                        onChange={(e) => handleFieldChange('label', e.target.value)}
                    />
                </div>
            </div>}

            {mode !== 'view' && <><div className="row">
                <div className="dropdown-section" style={{ flex: 1 }}>
                    <div className="dropdown-heading">Off Hysteresis (ms)</div>
                    {numberInput('offHysterisisMs', 'eg. 500')}
                </div>
                <div className="dropdown-section" style={{ flex: 1 }}>
                    <div className="dropdown-heading">On Hysteresis (ms)</div>
                    {numberInput('onHysterisisMs', 'eg. 200')}
                </div>
            </div>

            <div className="row">
                <div className="dropdown-section" style={{ flex: 1 }}>
                    <div className="dropdown-heading">Start Trigger Rule ID <span className="required-asterisk">*</span></div>
                    <Select
                        className="multi-select"
                        classNamePrefix="select"
                        styles={selectStyles}
                        options={contextRuleIdOptions}
                        value={form.startTriggerRuleId}
                        onChange={(val) => handleFieldChange('startTriggerRuleId', val)}
                        placeholder="Search Context Rule IDs"
                        isClearable
                        isSearchable
                        isDisabled={isCronBased}
                    />
                </div>
                <div className="dropdown-section" style={{ flex: 1 }}>
                    <div className="dropdown-heading">Stop Trigger Rule ID <span style={{ color: '#999', fontSize: '11px' }}>(or Stop Trigger Duration)</span></div>
                    <Select
                        className="multi-select"
                        classNamePrefix="select"
                        styles={selectStyles}
                        options={contextRuleIdOptions}
                        value={form.stopTriggerRuleId}
                        onChange={(val) => handleFieldChange('stopTriggerRuleId', val)}
                        placeholder="Search Context Rule IDs"
                        isClearable
                        isSearchable
                        isDisabled={isCronBased}
                    />
                </div>
                <div className="dropdown-section" style={{ flex: 1 }}>
                    <div className="dropdown-heading">Stop Trigger Duration (ms) <span style={{ color: '#999', fontSize: '11px' }}>(or Stop Trigger Rule ID)</span></div>
                    <input
                        className="file-input"
                        type="number"
                        min="0"
                        placeholder="eg. 500"
                        value={form.stopTriggerDurationMs}
                        onChange={(e) => handleFieldChange('stopTriggerDurationMs', e.target.value)}
                        disabled={isCronBased}
                        style={isCronBased ? { cursor: 'not-allowed' } : undefined}
                    />
                </div>
            </div>

            <div className="row" style={{ alignItems: 'center', marginBottom: '12px' }}>
                <div style={{
                    display: 'inline-flex',
                    background: '#f0f0f0',
                    borderRadius: '20px',
                    padding: '3px',
                    gap: '2px',
                }}>
                    <button
                        type="button"
                        onClick={() => handleFieldChange('quotaType', QUOTA_TYPE_MAX_SIZE)}
                        style={{
                            padding: '5px 14px',
                            borderRadius: '16px',
                            border: 'none',
                            background: form.quotaType === QUOTA_TYPE_MAX_SIZE ? '#1a4fba' : 'transparent',
                            color: form.quotaType === QUOTA_TYPE_MAX_SIZE ? 'white' : 'rgba(0,0,0,0.65)',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: form.quotaType === QUOTA_TYPE_MAX_SIZE ? 600 : 400,
                            whiteSpace: 'nowrap',
                            transition: 'background 0.2s, color 0.2s',
                        }}
                    >
                        Max Size (bytes)
                    </button>
                    <button
                        type="button"
                        onClick={() => handleFieldChange('quotaType', QUOTA_TYPE_MAX_OCCURRENCE)}
                        style={{
                            padding: '5px 14px',
                            borderRadius: '16px',
                            border: 'none',
                            background: form.quotaType === QUOTA_TYPE_MAX_OCCURRENCE ? '#1a4fba' : 'transparent',
                            color: form.quotaType === QUOTA_TYPE_MAX_OCCURRENCE ? 'white' : 'rgba(0,0,0,0.65)',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: form.quotaType === QUOTA_TYPE_MAX_OCCURRENCE ? 600 : 400,
                            whiteSpace: 'nowrap',
                            transition: 'background 0.2s, color 0.2s',
                        }}
                    >
                        Max Occurrence Count
                    </button>
                </div>
            </div>
            <div className="row">
                {form.quotaType === QUOTA_TYPE_MAX_SIZE ? (
                    <div className="dropdown-section" style={{ flex: 1 }}>
                        <div className="dropdown-heading">Quota Max Size (bytes)</div>
                        {numberInput('quotaMaxSize', 'eg. 638574')}
                    </div>
                ) : (
                    <div className="dropdown-section" style={{ flex: 1 }}>
                        <div className="dropdown-heading">Quota Max Occurrence Count</div>
                        {numberInput('quotaMaxOccuranceCount', 'eg. 638574')}
                    </div>
                )}
                <div className="dropdown-section" style={{ flex: 1 }}>
                    <div className="dropdown-heading">Quota Time Window (s)</div>
                    {numberInput('quotaTimeWindowMs', 'eg. 20000')}
                </div>
                <div className="dropdown-section" style={{ flex: 1 }}>
                    <div className="dropdown-heading">Quota Max Time Window (ms)</div>
                    {numberInput('quotaMaxTimeWindowMs', 'eg. 120000')}
                </div>
            </div>

            <div className="submit-button-section">
                <button
                    className="submit-button"
                    onClick={handleSubmit}
                    disabled={submitting || !form.ruleType || !form.label.trim() || !form.startTriggerRuleId || (!form.stopTriggerRuleId && form.stopTriggerDurationMs === '')}
                >
                    {submitting ? 'Submitting...' : 'Add Rule'}
                </button>
                <button
                    onClick={handleReset}
                    style={{ marginLeft: '12px', background: 'none', border: '1.5px solid transparent', color: '#6b7280', fontSize: '13px', fontWeight: 500, padding: '8px 20px', borderRadius: '6px', cursor: 'pointer' }}
                >
                    Reset
                </button>
            </div>
            </>}

            {/* ── Onboarded Rules Table ── */}
            {mode !== 'add' && <><div className="data-table-wrapper" style={{ minWidth: 'unset' }}>
                <table style={{ minWidth: 'unset', tableLayout: 'auto' }}>
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Label</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loadingRules ? (
                            <tr>
                                <td colSpan={3} className="table-loading">Loading...</td>
                            </tr>
                        ) : rules.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="table-empty">No context collection rules onboarded yet.</td>
                            </tr>
                        ) : (
                            rules.map((rule, idx) => (
                                <React.Fragment key={rule.id ?? idx}>
                                    <tr className={expandedRuleId === (rule.id ?? idx) ? 'expanded-row' : ''}>
                                        <td>{rule.ruleType}</td>
                                        <td>{rule.label || '—'}</td>
                                        <td>
                                            <button
                                                className="add-criteria-button"
                                                onClick={() => handleToggleExpand(rule.id ?? idx)}
                                            >
                                                {expandedRuleId === (rule.id ?? idx) ? 'Close' : 'View'}
                                            </button>
                                        </td>
                                    </tr>
                                    {expandedRuleId === (rule.id ?? idx) && (
                                        <tr className="expanded-details-row">
                                            <td colSpan={3}>
                                                <div className="expanded-row-content">
                                                    <div className="expanded-details-cards">
                                                        <div className="expanded-detail-card">
                                                            <h4>Rule Info</h4>
                                                            <div className="expanded-detail-card-content">
                                                                <DetailRow label="Rule Type" value={rule.ruleType} />
                                                                <DetailRow label="Label" value={rule.label} />
                                                            </div>
                                                        </div>
                                                        <div className="expanded-detail-card">
                                                            <h4>Hysteresis</h4>
                                                            <div className="expanded-detail-card-content">
                                                                <DetailRow label="Off Hysteresis (ms)" value={rule.offHysterisisMs} />
                                                                <DetailRow label="On Hysteresis (ms)" value={rule.onHysterisisMs} />
                                                            </div>
                                                        </div>
                                                        <div className="expanded-detail-card">
                                                            <h4>Trigger</h4>
                                                            <div className="expanded-detail-card-content">
                                                                <DetailRow label="Start Trigger Rule ID" value={rule.startTriggerRuleId} />
                                                                <DetailRow label="Stop Trigger Rule ID" value={rule.stopTriggerRuleId} />
                                                                <DetailRow label="Stop Trigger Duration (ms)" value={rule.stopTriggerDurationMs} />
                                                            </div>
                                                        </div>
                                                        <div className="expanded-detail-card">
                                                            <h4>Quota</h4>
                                                            <div className="expanded-detail-card-content">
                                                                <DetailRow label="Max Size (bytes)" value={rule.quotaMaxSize} />
                                                                <DetailRow label="Max Occurrence Count" value={rule.quotaMaxOccuranceCount} />
                                                                <DetailRow label="Time Window (s)" value={rule.quotaTimeWindowSec} />
                                                                <DetailRow label="Max Time Window (ms)" value={rule.quotaMaxTimeWindowMs} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            </>}
        </div>
    );
}

function DetailRow({ label, value }) {
    const display = value != null && value !== '' ? String(value) : '—';
    return (
        <div className="expanded-detail-item">
            <strong>{label}</strong>
            <span>{display}</span>
        </div>
    );
}

export function RuleDetailCard({ rule }) {
    if (!rule) return null;
    return (
        <div className="expanded-details-cards">
            <div className="expanded-detail-card">
                <h4>Rule Info</h4>
                <div className="expanded-detail-card-content">
                    <DetailRow label="Rule Type" value={rule.ruleType} />
                    <DetailRow label="Label"     value={rule.label} />
                </div>
            </div>
            <div className="expanded-detail-card">
                <h4>Hysteresis</h4>
                <div className="expanded-detail-card-content">
                    <DetailRow label="Off Hysteresis (ms)" value={rule.offHysterisisMs} />
                    <DetailRow label="On Hysteresis (ms)"  value={rule.onHysterisisMs} />
                </div>
            </div>
            <div className="expanded-detail-card">
                <h4>Trigger</h4>
                <div className="expanded-detail-card-content">
                    <DetailRow label="Start Trigger Rule ID"      value={rule.startTriggerRuleId} />
                    <DetailRow label="Stop Trigger Rule ID"       value={rule.stopTriggerRuleId} />
                    <DetailRow label="Stop Trigger Duration (ms)" value={rule.stopTriggerDurationMs} />
                </div>
            </div>
            <div className="expanded-detail-card">
                <h4>Quota</h4>
                <div className="expanded-detail-card-content">
                    <DetailRow label="Max Size (bytes)"     value={rule.quotaMaxSize} />
                    <DetailRow label="Max Occurrence Count" value={rule.quotaMaxOccuranceCount} />
                    <DetailRow label="Time Window (s)"    value={rule.quotaTimeWindowSec} />
                    <DetailRow label="Max Time Window (ms)" value={rule.quotaMaxTimeWindowMs} />
                </div>
            </div>
        </div>
    );
}

export default ContextCollectionRules;
